interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

export async function uploadWithRetry<T>(
  uploadFn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const { maxRetries = 3, retryDelay = 1000, onRetry } = options;
  
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await uploadFn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (attempt < maxRetries) {
        onRetry?.(attempt, lastError);
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
      }
    }
  }
  
  throw lastError!;
}

// Specific error types for better handling
export class UploadError extends Error {
  constructor(
    message: string,
    public code: 'NETWORK' | 'SIZE' | 'FORMAT' | 'SERVER' | 'AUTH',
    public recoverable: boolean = true
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

export function classifyError(error: any): UploadError {
  const message = error?.message || 'Unknown error';
  
  // Network errors
  if (message.includes('fetch') || message.includes('network') || message.includes('Failed to fetch')) {
    return new UploadError(
      'Network connection issue. Please check your internet and try again.',
      'NETWORK',
      true
    );
  }
  
  // Size errors
  if (message.includes('size') || message.includes('too large') || message.includes('exceeds')) {
    return new UploadError(
      'Image is too large. Please use an image under 5MB.',
      'SIZE',
      false
    );
  }
  
  // Auth errors
  if (message.includes('authenticated') || message.includes('auth') || message.includes('Not authenticated')) {
    return new UploadError(
      'Session expired. Please refresh the page and try again.',
      'AUTH',
      true
    );
  }
  
  // Server errors
  if (message.includes('500') || message.includes('server') || message.includes('Internal server error')) {
    return new UploadError(
      'Server error. Our team has been notified. Please try again later.',
      'SERVER',
      true
    );
  }
  
  // Convex specific errors
  if (message.includes('storage') || message.includes('Storage ID')) {
    return new UploadError(
      'Upload service error. Please try again.',
      'SERVER',
      true
    );
  }
  
  return new UploadError(message, 'SERVER', true);
} 